import { Offer } from '../model/offer.types';
import { reportMoney, toMoney } from '~shared/api/assert-money';
import type { OneGetOffersCommand, OneOffersDTO } from '~shared/api/schemas';
import { Configuration } from '~entities/configuration/model/configuration.types';
import { CartItem } from '~entities/session/model/session.types';
import { oneIdentifier } from '~shared/config/hostname';
import { Room } from '~entities/room/model/room.types';
import { BookingRoom } from '~processes/booking/model/booking.types';
import { LastMinutePackage } from '~entities/package/model/package.types';
import { queryClient } from '~shared/query-client';
import i18n, { Locale } from '~app/i18n';
import { configurationQueryOptions } from '~entities/configuration/api/configuration.queries';

export const transformBookingRoomToToOffersQueryDto = (
  bookingRoom: BookingRoom,
): OneGetOffersCommand => ({
  adults: bookingRoom.occupancy.adults,
  children: bookingRoom.occupancy.children,
  arrivalDay: bookingRoom.offer.arrivalDay,
  nights: bookingRoom.offer.nights,
  oneIdentifier,
});

export const transformLastMinutePackageToOffer = (
  lastMinutePackage: LastMinutePackage,
): Offer => ({
  type: 'LAST_MINUTE_PACKAGE',
  arrivalDay: lastMinutePackage.arrivalDay,
  nights: lastMinutePackage.duration,
});

/**
 * `departureDay` and `minimumPrice` are deliberately absent: the old hand-written Zod schema
 * declared both as optional, but OneOffersController never sends either, so both mappings have
 * always produced `undefined`. Only the calendar teaser DTO carries them (samera/samera#3319).
 *
 * The price is reported, not asserted: an offer list is a shop window. A malformed amount on one
 * offer must not throw away the other twenty, and the guest reaches payment only through the
 * session, whose money guards do throw.
 *
 * Unlike the calendar teaser, an ABSENT price is reported here too, and that is deliberate:
 * `OneOfferDTO.price` is a non-null Kotlin `Money`, so the contract says it is always sent and its
 * absence is a real break worth hearing about. The calendar's `teaserPrice` skips absent values
 * because `minimumDayPrice`/`minimumPrice` are genuinely nullable there. The deleted Zod schema
 * declared this one `.optional()`, but that was a hand-written guess, not the contract.
 */
export const transformOffersDtoToOffers = (
  offersDto: OneOffersDTO,
): Offer[] => {
  return offersDto.offers.map(offer => ({
    type: offer.type,
    arrivalDay: offer.arrivalDay,
    nights: offer.nights,
    adults: offer.adults,
    children: offer.children,
    price: reportMoney(
      offer.price,
      `offers[${offer.type}:${offer.roomCategoryId}].price`,
    )
      ? toMoney(offer.price)
      : undefined,
    overlayMessages: offer.overlayMessages,
    bookable: offer.bookable,
    roomId: offer.roomCategoryId,
    lastMinutePackageId: offer.lastMinutePackageId,
    packageId: offer.packageGroupId,
    packageCategoryId: offer.packageCategoryId,
    rateId: offer.rateId,
  }));
};

export const populateOfferWithRate =
  (configuration: Configuration) => (offer: Offer) => ({
    ...offer,
    rate: configuration.rates.find(({ id }) => id === offer.rateId),
  });

export const populateOffersWithRate = (offers: Offer[]) => {
  const configuration = queryClient.getQueryData(
    configurationQueryOptions(i18n.language as Locale).queryKey,
  );

  if (!configuration) return;

  return offers.map(populateOfferWithRate(configuration));
};

export const transformCartItemToBookingRoom = (
  cartItem: CartItem,
): BookingRoom => ({
  id: cartItem.id,
  cartItemId: cartItem.id,
  occupancy: {
    adults: cartItem.guests.reduce(
      (a, guest) => a + (guest.type === 'ADULT' ? 1 : 0),
      0,
    ),
    children: cartItem.guests
      .filter(guest => guest.type === 'CHILD')
      .map(({ childAge }) => childAge ?? 0),
  },
  offer: cartItem,
  offeringId:
    cartItem.type === 'LAST_MINUTE_PACKAGE'
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        cartItem.lastMinutePackageId!
      : cartItem.type === 'PACKAGE'
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        cartItem.packageId!
      : cartItem.roomId,
});

export const groupOffersByRooms = (
  configuration: Configuration,
  offers: Offer[],
) => {
  return offers?.reduce<(Room & { offers: Offer[] })[]>((acc, offer) => {
    if (!offer.roomId) return acc;
    const existingRoom = acc.find(({ id }) => id === offer.roomId);
    if (existingRoom) {
      existingRoom.offers.push(offer);
      return acc;
    }

    const room = configuration.rooms.find(({ id }) => id === offer.roomId);

    if (!room) return acc;

    acc.push({
      ...room,
      offers: [offer],
    });

    return acc;
  }, []);
};
